import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { STUDENT_NAV } from "@/lib/studentNav";
import { DashboardShell, StatCard } from "@/components/DashboardShell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, FileText, ScanLine, Award, CheckCircle2, Clock, Eye, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import QuestionPaperViewer from "@/components/QuestionPaperViewer";

const NAV = STUDENT_NAV;

const StatusBadge = ({ status }) => {
  const map = {
    queued: { v: "secondary", i: Clock, t: "Queued" },
    uploaded: { v: "secondary", i: Clock, t: "Uploaded" },
    evaluating: { v: "secondary", i: Clock, t: "Evaluating…" },
    evaluated: { v: "default", i: CheckCircle2, t: "Evaluated" },
    failed: { v: "destructive", i: Clock, t: "Failed" },
  };
  const c = map[status] || map.uploaded;
  const I = c.i;
  return <Badge variant={c.v} className="gap-1"><I className="h-3 w-3" />{c.t}</Badge>;
};

export default function StudentDashboard() {
  const [stats, setStats] = useState(null);
  const [sub, setSub] = useState(null);
  const [papers, setPapers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [viewingPaper, setViewingPaper] = useState(null);

  const load = () => {
    api.get("/stats/student").then((r) => setStats(r.data)).catch(() => {});
    api.get("/subscriptions/me").then((r) => setSub(r.data)).catch(() => {});
    api.get("/question-papers").then((r) => setPapers(r.data)).catch(() => {});
    api.get("/answer-scripts").then((r) => setScripts(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const timer = setInterval(() => {
      api.get("/answer-scripts").then((r) => setScripts(r.data)).catch(() => {});
    }, 5000);
    window.addEventListener("evaluation-updated", load);
    return () => {
      clearInterval(timer);
      window.removeEventListener("evaluation-updated", load);
    };
  }, []);

  const remaining = sub?.remaining ?? 0;
  const pendingPaperIds = new Set(scripts.filter((s) => ["queued", "evaluating"].includes(s.status)).map((s) => s.paper_id));
  const completedPaperIds = new Set(scripts.filter((s) => s.status === "evaluated").map((s) => s.paper_id));

  return (
    <DashboardShell nav={NAV} title="My Dashboard">
      <QuestionPaperViewer paper={viewingPaper} open={Boolean(viewingPaper)} onOpenChange={(isOpen) => { if (!isOpen) setViewingPaper(null); }} />
      <div className="flex flex-col">
      <div className="order-2 mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-6 lg:order-none lg:mt-0">
        <StatCard testId="stat-quota" label="Scripts remaining" value={remaining} sub={`${sub?.used || 0} used of ${sub?.quota || 0}`} icon={CreditCard} />
        <StatCard testId="stat-submitted" label="Scripts submitted" value={stats?.totals.scripts_total ?? 0} icon={FileText} />
        <StatCard testId="stat-evaluated" label="Evaluated" value={stats?.totals.scripts_evaluated ?? 0} icon={CheckCircle2} accent="text-[hsl(var(--chart-2))]" />
        <StatCard testId="stat-avg" label="Average score" value={`${stats?.totals.avg_score_pct ?? 0}%`} icon={Award} accent="text-[hsl(var(--chart-3))]" />
      </div>

      {stats?.performance?.length > 0 && (
        <div className="order-3 mt-6 bg-background border border-border rounded-lg p-6 lg:order-none">
          <p className="overline text-muted-foreground mb-4">Performance trend</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
              <LineChart data={stats.performance}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {stats?.by_subject?.length > 0 && (
        <div className="order-4 mt-6 bg-background border border-border rounded-lg p-6 lg:order-none" data-testid="progress-by-subject">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-primary" /><p className="overline text-muted-foreground">Progress by subject</p></div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
                <BarChart data={stats.by_subject}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="subject" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip />
                  <Bar dataKey="avg" name="Avg score %" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 content-start">
              {stats.by_subject.map((s) => {
                const up = s.improvement > 0, down = s.improvement < 0;
                const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
                const color = up ? "text-[hsl(var(--chart-2))]" : down ? "text-destructive" : "text-muted-foreground";
                return (
                  <div key={s.subject} data-testid={`subject-progress-${s.subject}`} className="border border-border rounded-md p-4">
                    <p className="font-medium truncate">{s.subject}</p>
                    <p className="font-heading font-black text-2xl mt-1">{s.avg}%</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                      <span>{s.attempts} attempt{s.attempts !== 1 ? "s" : ""} &middot; best {s.best}%</span>
                      <span className={`inline-flex items-center gap-1 font-medium ${color}`}>
                        <Icon className="h-3.5 w-3.5" /> {s.improvement > 0 ? "+" : ""}{s.improvement}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="order-1 grid gap-6 lg:order-none lg:mt-6 lg:grid-cols-2">
        <div className="bg-background border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="overline text-muted-foreground">Available exams</p>
            {(remaining <= 0 || papers.length === 0) && <Link to="/student/plans"><Button size="sm">Buy papers</Button></Link>}
          </div>
          <div className="space-y-3">
            {papers.length === 0 && (
              <div className="text-sm text-muted-foreground" data-testid="no-papers-empty">
                {remaining <= 0 ? (
                  <>No exams unlocked. <Link to="/student/plans" className="text-primary font-medium">Buy papers</Link> to add question papers to your dashboard.</>
                ) : (
                  "No exams available for your subscription yet."
                )}
              </div>
            )}
            {papers.map((p) => {
              const pending = pendingPaperIds.has(p.id);
              const completed = completedPaperIds.has(p.id);
              const quantityPurchase = Number.isInteger(p.remaining_sets);
              const exhausted = quantityPurchase ? p.remaining_sets <= 0 : completed;
              return (
              <div key={p.id} data-testid={`paper-${p.id}`} className="flex flex-col gap-3 border border-border rounded-md p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.subject} &middot; {p.board || "All boards"} &middot; {p.language} &middot; {p.total_marks} marks</p>
                  {quantityPurchase && <p className="mt-1 text-xs font-medium text-primary">{p.remaining_sets} of {p.purchased_sets} set{p.purchased_sets === 1 ? "" : "s"} remaining</p>}
                </div>
                <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
                  {p.question_paper_pdf && (
                    <Button size="sm" variant="outline" className="flex-1 gap-1.5 rounded-full sm:flex-none" onClick={() => setViewingPaper(p)} data-testid={`view-paper-${p.id}`}>
                      <Eye className="h-4 w-4" /> View
                    </Button>
                  )}
                  <Link
                    to={`/student/attempt/${p.id}/instructions`}
                    className="flex-1 sm:flex-none"
                    aria-disabled={remaining <= 0 || exhausted || pending}
                    onClick={(event) => { if (remaining <= 0 || exhausted || pending) event.preventDefault(); }}
                  >
                    <Button size="sm" className="w-full gap-2 rounded-full sm:w-auto" disabled={remaining <= 0 || exhausted || pending} data-testid={`attempt-${p.id}`}>
                      {pending ? <><Clock className="h-4 w-4" /> Evaluating</> : exhausted ? <><CheckCircle2 className="h-4 w-4" /> Completed</> : completed ? <><ScanLine className="h-4 w-4" /> Attempt again ({p.remaining_sets} left)</> : <><ScanLine className="h-4 w-4" /> Attempt</>}
                    </Button>
                  </Link>
                </div>
              </div>
            );})}
          </div>
        </div>

        {/* My scripts */}
        <div className="bg-background border border-border rounded-lg p-6">
          <p className="overline text-muted-foreground mb-4">My answer scripts</p>
          <div className="space-y-3">
            {scripts.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
            {scripts.map((s) => (
              <div key={s.id} data-testid={`script-${s.id}`} className="flex items-center justify-between border border-border rounded-md p-4 hover:border-primary transition-colors">
                <Link to={`/result/${s.id}`} className="flex-1 min-w-0">
                  <p className="font-medium truncate">{s.paper_title}</p>
                  <p className="text-xs text-muted-foreground">{s.subject} &middot; {s.page_count} pages</p>
                </Link>
                <div className="flex items-center gap-3 shrink-0">
                  {s.status === "evaluated" && s.result && (
                    <span className="font-heading font-bold">{s.result.total_awarded}/{s.result.total_max}</span>
                  )}
                  <StatusBadge status={s.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </DashboardShell>
  );
}
